import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import PropertyForm from '../components/PropertyForm';
import Map from '../components/Map';
import EstimatePanel from '../components/EstimatePanel';
import { calculateEstimate } from '../lib/estimator';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTranslation } from 'react-i18next';

const { FiMapPin, FiHome, FiCalculator } = FiIcons;

const Simulator = () => {
  const [formData, setFormData] = useState({});
  const [estimate, setEstimate] = useState(null);
  const [coordinates, setCoordinates] = useState(null);
  const [address, setAddress] = useState('');
  const [progress, setProgress] = useState(10);
  const { t } = useTranslation();

  // Calculate progress based on required fields
  useEffect(() => {
    const requiredFields = [
      'propertyType', 'address', 'sqMeters', 'rooms', 'bathrooms', 'guests', 'periodType',
      'firstName', 'lastName', 'phone', 'email', 'gdprConsent'
    ];
    
    const filledFields = requiredFields.filter(field => {
      if (field === 'address') return address;
      if (field === 'gdprConsent') return formData[field] === true;
      return formData[field];
    });
    
    const newProgress = Math.max(10, Math.round((filledFields.length / requiredFields.length) * 100));
    setProgress(newProgress);
  }, [formData, address]);

  const handleFormChange = (data) => {
    setFormData(data);
  };

  const handleCalculateEstimate = () => {
    if (!formData.propertyType || !address || !formData.sqMeters || !formData.rooms) {
      return;
    }
    
    const estimateData = { ...formData, address, coordinates };
    const result = calculateEstimate(estimateData);
    setEstimate(result);
    
    // Scroll to results
    setTimeout(() => {
      document.getElementById('estimate-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className="pt-16 min-h-screen" style={{ backgroundColor: '#d8eeff' }}>
      {/* Hero Section */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 shadow-lg"
              style={{ backgroundColor: '#ff7f50' }}
            >
              <SafeIcon icon={FiCalculator} className="h-10 w-10 text-white" />
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold mb-6"
              style={{ color: '#0b294b', fontFamily: '"Knockout Featherweight","Poppins",system-ui,sans-serif' }}
            >
              {t('Scopri il potenziale del tuo immobile')}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto"
              style={{ color: '#0b294b' }}
            >
              {t('Inserisci pochi dati e ricevi una stima preliminare del tuo guadagno con gli affitti brevi')}
            </motion.p>
            
            {/* Progress Bar */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }} 
              animate={{ opacity: 1, scale: 1 }} 
              transition={{ duration: 0.6, delay: 0.3 }}
              className="max-w-md mx-auto mb-8"
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium" style={{ color: '#0b294b' }}>{t('Progresso')}</span>
                <span className="text-sm font-medium" style={{ color: '#ff7f50' }}>{progress}%</span>
              </div>
              <div className="w-full bg-white/50 rounded-full h-3 shadow-inner">
                <div 
                  className="h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
                  style={{ 
                    width: `${progress}%`, 
                    backgroundColor: '#ff7f50' 
                  }}
                />
              </div>
            </motion.div>
            
            {/* Benefits */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap justify-center gap-6 text-sm font-medium"
              style={{ color: '#0b294b' }}
            >
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiCalculator} className="h-4 w-4" style={{ color: '#ff7f50' }} />
                <span>{t('Stima indicativa')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiHome} className="h-4 w-4" style={{ color: '#ff7f50' }} />
                <span>{t('Nessun impegno')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiMapPin} className="h-4 w-4" style={{ color: '#ff7f50' }} />
                <span>{t('Risultato immediato')}</span>
              </div>
            </motion.div>
          </div>
          
          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Form Section */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <PropertyForm 
                onFormChange={handleFormChange}
                onAddressChange={setAddress}
                onCoordinatesChange={setCoordinates}
                onCalculate={handleCalculateEstimate}
                formData={formData}
                address={address}
                canCalculate={progress >= 60}
              />
            </motion.div>
            
            {/* Map and Results Section */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.6, delay: 0.7 }}
              className="space-y-6"
            >
              {/* Map */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="text-lg font-semibold" style={{ color: '#0b294b' }}>
                    {t('Posizione immobile')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {address || t("Inserisci l'indirizzo nel form per vedere la mappa")}
                  </p>
                </div>
                <div className="h-64">
                  <Map 
                    coordinates={coordinates} 
                    onCoordinatesChange={setCoordinates} 
                    address={address} 
                    onAddressChange={setAddress} 
                  />
                </div>
              </div>
              
              {/* Estimate Results */}
              {estimate && (
                <div id="estimate-results">
                  <EstimatePanel 
                    estimate={estimate} 
                    formData={{ ...formData, address, coordinates }} 
                  />
                </div>
              )}
            </motion.div>
          </div>
          
          {/* Legal Notice */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }} 
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 max-w-4xl mx-auto shadow-sm">
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong>{t('Nota legale:')}</strong> {t("La stima è puramente indicativa e non costituisce consulenza né garanzia di risultato. I valori possono variare in base a localizzazione precisa,stagionalità,eventi,concorrenza e gestione. Per una valutazione accurata,consigliamo una consulenza personalizzata.")}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Simulator;